import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { Application, ApplicationWithToken } from '@comms/shared';
import { useStore } from '../../store';
import { api } from '../../lib/api';
import { Avatar, Button, ErrorText, Input, Spinner } from '../ui';

/** Small "BOT" tag shown next to bot names. */
export function BotTag() {
  return (
    <span className="ml-1 rounded bg-accent px-1 align-middle text-[10px] font-bold leading-4 text-white">
      BOT
    </span>
  );
}

/** A token shown once, with a copy button. */
function RevealedToken({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-2 rounded border border-warning/40 bg-warning/10 p-3">
      <p className="text-sm text-text-1">
        Copy this token now. It won't be shown again (you can reset it later). Anyone with it can
        act as your bot.
      </p>
      <div className="flex gap-2">
        <Input readOnly aria-label="Bot token" value={token} onFocus={(e) => e.target.select()} />
        <Button
          onClick={() => void navigator.clipboard.writeText(token).then(() => setCopied(true))}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

/** User settings → Developer: create and manage your bots. */
export function DeveloperSettings() {
  const [apps, setApps] = useState<Application[] | null>(null);
  const [name, setName] = useState('');
  const [revealed, setRevealed] = useState<{ id: string; token: string } | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = () =>
    api<Application[]>('GET', '/applications').then(setApps, (err: unknown) => {
      setError(err);
      setApps([]);
    });
  useEffect(() => {
    void load();
  }, []);

  const reveal = (r: ApplicationWithToken) => setRevealed({ id: r.application.id, token: r.token });

  async function create() {
    try {
      reveal(await api<ApplicationWithToken>('POST', '/applications', { name: name.trim() }));
      setName('');
      await load();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-3">
        Bots are accounts your programs control with a token. They can join servers when someone who
        can manage the server adds them, and then follow that server's roles. Bots can't use DMs,
        voice or end-to-end encrypted features.
      </p>
      <div className="flex items-end gap-2">
        <Input
          label="New bot name"
          value={name}
          minLength={2}
          maxLength={32}
          placeholder="Helper"
          onChange={(e) => setName(e.target.value)}
        />
        <Button disabled={name.trim().length < 2} onClick={create}>
          Create bot
        </Button>
      </div>
      <ErrorText error={error} />
      {!apps && <Spinner />}
      {apps?.length === 0 && <p className="text-sm text-text-3">You don't have any bots yet.</p>}
      {apps?.map((a) => (
        <div key={a.id} className="space-y-3 rounded bg-bg-1 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-40 flex-1">
              <div className="font-medium text-text-0">
                {a.name}
                <BotTag />
              </div>
              <div className="text-xs text-text-3">
                ID <code className="select-all">{a.id}</code>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-text-2">
              <input
                type="checkbox"
                checked={a.public}
                onChange={(e) =>
                  void api('PATCH', `/applications/${a.id}`, { public: e.target.checked }).then(
                    load,
                    setError,
                  )
                }
              />
              Public
            </label>
            <Button
              variant="ghost"
              onClick={() => {
                const next = prompt('Bot name', a.name);
                if (next?.trim() && next !== a.name)
                  void api('PATCH', `/applications/${a.id}`, { name: next.trim() }).then(
                    load,
                    setError,
                  );
              }}
            >
              Rename
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                confirm('Reset the token? The old one stops working immediately.') &&
                void api<ApplicationWithToken>('POST', `/applications/${a.id}/token`).then(
                  reveal,
                  setError,
                )
              }
            >
              Reset token
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                confirm(`Delete bot "${a.name}"? It leaves every server.`) &&
                void api('DELETE', `/applications/${a.id}`).then(load, setError)
              }
            >
              Delete
            </Button>
          </div>
          {revealed?.id === a.id && <RevealedToken token={revealed.token} />}
        </div>
      ))}
      <p className="text-xs text-text-3">
        "Public" lets other people add the bot to servers they manage. See the README for a minimal
        bot example.
      </p>
    </div>
  );
}

/** Server settings → Bots: add a bot by id, list and remove bots. */
export function GuildBots({ guildId }: { guildId: string }) {
  const bots = useStore(
    useShallow((s) => Object.values(s.members[guildId] ?? {}).filter((m) => m.user.bot)),
  );
  const [botId, setBotId] = useState('');
  const [mine, setMine] = useState<Application[]>([]);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    void api<Application[]>('GET', '/applications').then(setMine, () => setMine([]));
  }, []);

  const add = (id: string) =>
    api('PUT', `/guilds/${guildId}/bots/${id}`).then(() => {
      setBotId('');
      setError(null);
    }, setError);

  const addable = mine.filter((a) => !bots.some((b) => b.user.id === a.id));

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-text-0">Bots</h2>
      <p className="text-sm text-text-3">
        Add one of your bots, or a public bot by its ID. Bots get the @everyone role; give them more
        roles in Members.
      </p>
      {addable.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {addable.map((a) => (
            <Button key={a.id} variant="secondary" onClick={() => void add(a.id)}>
              Add {a.name}
            </Button>
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <Input
          label="Bot ID"
          value={botId}
          inputMode="numeric"
          placeholder="123456789012345678"
          onChange={(e) => setBotId(e.target.value.trim())}
        />
        <Button disabled={!/^\d+$/.test(botId)} onClick={() => void add(botId)}>
          Add bot
        </Button>
      </div>
      <ErrorText error={error} />
      {bots.length === 0 && <p className="text-sm text-text-3">No bots in this server.</p>}
      <div className="divide-y divide-bg-3">
        {bots.map((m) => (
          <div key={m.user.id} className="flex items-center gap-3 py-3">
            <Avatar userId={m.user.id} size={32} />
            <div className="flex-1 text-text-0">
              {m.nickname ?? m.user.displayName}
              <BotTag />
            </div>
            <Button
              variant="danger"
              onClick={() =>
                confirm(`Remove ${m.user.displayName} from this server?`) &&
                void api('DELETE', `/guilds/${guildId}/members/${m.user.id}`).catch(setError)
              }
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
